import PicabooConstants from '../../../../common/PicabooConstants';
import { GraphicItemUtils } from '../../../../utils/GraphicItemUtils';
import { hexToDecimal, decimalToRgba } from '../../../../utils/color';
import { fabric } from 'fabric';
import PageRendererRegistry from './PageRendererRegistry';
import PageRendererGraphicItemHelper from './PageRendererGraphicItemHelper';
import PageRendererEmptyWellHelper from './PageRendererEmptyWellHelper';
import PageRendererShapeHelper from './PageRendererShapeHelper';
import PageRendererRichTextHelper from './PageRendererRichTextHelper';
import ItemMetrics from './ItemMetrics';
import async from 'async-es';
import { fromJS, List, Map } from 'immutable';
import FabricUtils from '../../../../utils/FabricUtils';
import * as zoomServices from '../../../../utils/zoomServices';
import { isFabric } from '../../../../utils/subtypeHelper';
import { getFabricImage } from '../../../../utils/fabricImages';
import invariant from 'tiny-invariant';
import { z } from 'zod';

let nodeRender = false;
if (typeof window === 'undefined') {
  nodeRender = true;
}

let _renderRequestIDs = {}; //

//let _currentPageRenderID = 0;

export class PageRenderer extends ItemMetrics {
  /**
   * PageRenderer constructor.
   * @param canvas The fabric canvas.
   * @param pageDimensions An object with 'width' and 'height' properties.
   * @param interactive If true, items on the page can be selected, moved, resized, etc.
   * @param pageRenderID
   * @param textSelectionChanged
   * @param pageItemUpdated
   * @param productInfo
   * @param numPages
   * @param style
   * @param spineCaption
   * @param wrap
   * @param overlay
   * @param stamping
   * @param preferences
   * @param captionVersion
   * @param isMobile
   * @param projectPK
   * @param subType
   * @param foilColor
   * @param cover
   * @param projectWarnings
   * @param handleUpdateWarnings
   * @param pageSpreadID
   * @param withWarnings
   */
  constructor({
    canvas,
    pageDimensions,
    interactive,
    pageRenderID,
    textSelectionChanged,
    pageItemUpdated, //callback
    productInfo,
    numPages,
    style,
    spineCaption,
    wrap,
    overlay,
    stamping,
    preferences,
    captionVersion,
    isMobile,
    projectPK,
    subType,
    foilColor,
    cover,
    projectWarnings,
    handleUpdateWarnings,
    pageSpreadID,
    withWarnings,
  }) {
    super();
    this._canvas = canvas;
    this._dim = pageDimensions;
    this._interactive = interactive === undefined ? true : interactive;
    this._pageRenderID = pageRenderID;
    this._pageItems = [];
    this._textSelectionChanged = textSelectionChanged;
    this._register = true;
    this.pageItemUpdated = pageItemUpdated;
    this._imagePath = PicabooConstants.IMAGE_RESOLUTIONS.HIGH;
    this._productInfo = productInfo;
    this._numPages = numPages;
    this._style = style;
    this._subType = subType;
    this._spineCaption = spineCaption;
    this._wrap = wrap;
    this._overlay = overlay;
    this._stamping = stamping;
    this._preferences = preferences;
    this.captionVersion =
      captionVersion == undefined
        ? PicabooConstants.CAPTION_VERSION.RICHTEXT
        : captionVersion;
    this.isMobile = isMobile;
    this._projectPK = projectPK;
    this._foilColor = foilColor;
    this._coverColor = cover;
    this._projectWarnings = projectWarnings;
    this._handleUpdateWarnings = handleUpdateWarnings;
    this.pageSpreadID = pageSpreadID;
    this._withWarnings = withWarnings;
  }

  set imagePath(path) {
    this._imagePath = path;
  }

  static setCurrentPageRenderID(pageID, value) {
    _renderRequestIDs[pageID] = value;
  }

  static getCurrentPageRenderID(pageID) {
    return _renderRequestIDs[pageID];
  }

  get currentPageDimensions() {
    return this._dim || { width: 0, height: 0 };
  }

  clearCanvas() {
    this._canvas.clear();
  }

  static calcPageDimensions(
    canvasContainer,
    projectRatio,
    projectType = -1,
    ratioBase,
  ) {
    //ratio = height/width
    //height = ratio*width
    //width = height/ratio

    // Container dimensions
    let cwidth = canvasContainer.clientWidth;
    let cheight = canvasContainer.clientHeight;
    let ratio = projectRatio || 1.0;

    let d = {
      width: 0,
      height: 0,
      scale: 1,
      uswidth: PicabooConstants.PAGE.UNSCALED_PAGE_WIDTH,
      ratio: ratio,
    };

    if (ratio / ratioBase > 1.0) {
      //Portrait orientation
      d.height = Math.round(cheight);
      d.width = Math.round((cheight / ratio) * ratioBase);
      if (d.height < cheight) {
        d.height = Math.round(cheight);
        d.width = Math.round((d.height / ratio) * ratioBase);
      }
    } else {
      //Landscape orientation
      d.width = Math.round(cwidth);
      d.height = Math.round((ratio * cwidth) / ratioBase);
      if (d.width < cwidth) {
        d.width = Math.round(cwidth);
        d.height = Math.round((d.width * ratio) / ratioBase);
      }
    }

    const isCard = projectType === PicabooConstants.PRODUCT_TYPES.CARD;

    this._ratioBase = ratioBase || this._ratioBase;

    d.ratioWidth = d.width / this._ratioBase;

    d.scale =
      (d.ratioWidth > d.height || isCard ? d.ratioWidth : d.height) /
      PicabooConstants.PAGE.UNSCALED_PAGE_WIDTH;
    d.zoom = d.width / PicabooConstants.PAGE.UNSCALED_PAGE_WIDTH;

    return d;
  }

  getCanvasItem(pageItemID) {
    let canvas = this._canvas;
    let objects = canvas.getObjects();
    for (let obj of objects) {
      if (obj.pageItemVO && obj.pageItemVO.get('id') === pageItemID) {
        return obj;
      }
    }

    return null;
  }

  updateInteractive(interactive) {
    let canvas = this._canvas;

    //Remove any selections
    canvas.discardActiveObject();

    let objects = canvas.getObjects();
    for (let obj of objects) {
      obj.defaultSelectable =
        obj.defaultSelectable === undefined
          ? obj.selectable
          : obj.defaultSelectable;
      obj.selectable = interactive && obj.defaultSelectable;
      // obj.set({selectable: interactive && obj.defaultSelectable, defaultSelectable: obj.selectable || interactive});
    }

    canvas.requestRenderAll();
  }

  selectAll() {
    let canvas = this._canvas;

    canvas.discardActiveObject();
    let objects = this.getObjects();
    if (objects.length > 0) {
      let sel = new fabric.ActiveSelection(objects, {
        canvas: canvas,
      });

      canvas.setActiveObject(sel);
      canvas.requestRenderAll();
    }
  }

  /**
   * Returns all objects EXCEPT spine caption stuff and page numbers.
   */
  getObjects() {
    let canvas = this._canvas;
    let array = [];
    let objects = canvas.getObjects();
    for (let obj of objects) {
      if (!obj.selectable) {
        continue;
      }
      array.push(obj);
    }

    return array;
  }

  selectMulti(ids) {
    let canvas = this._canvas;

    canvas.discardActiveObject();
    let objects = canvas.getObjects();
    let selected = [];
    for (let obj of objects) {
      if (obj.pageItemVO && ids.includes(obj.pageItemVO.get('id'))) {
        selected.push(obj);
      }
    }

    if (selected.length == 1) {
      canvas.setActiveObject(selected[0]);
    } else if (selected.length > 0) {
      let sel = new fabric.ActiveSelection(selected, {
        canvas: canvas,
      });

      canvas.setActiveObject(sel);
      canvas.requestRenderAll();
    }
  }

  clearSelection() {
    this._canvas.discardActiveObject().requestRenderAll();
  }

  redrawBackground(gi, callback) {
    let helper = new PageRendererGraphicItemHelper(
      this.currentPageDimensions,
      this._canvas,
      this._images,
      this._imagePath,
      this._ratioWidth,
      this._productInfo && this._productInfo.get('resolutionFactor'),
      this._interactive,
      nodeRender,
      this.isMobile,
    );

    helper.drawBackgroundImage(gi).then((ret) => {
      if (ret.fail) {
        console.error('Missing Image', gi ? gi.toJS() : null);
        return;
      }

      const image = ret.image;
      if (image !== null) {
        if (image.pageItemVO.get('pageID') != this._canvas.pageID) {
          //we changed pages during the load, so just drop this
          return;
        }
        this._canvas.setBackgroundImage(image);
        this._canvas.requestRenderAll();
        callback && callback();
      }
    });
  }

  applyStyleToCaption(pageItem, style, position) {
    let helper = new PageRendererRichTextHelper(
      this.currentPageDimensions,
      this._canvas,
      this._ratioWidth,
      this._interactive,
      this._productInfo,
      nodeRender,
      this.isMobile,
    );
    pageItem = this._applyFoilColor(pageItem);
    helper.applyStyleToCaption(pageItem, style, position);
    this._canvas.renderAll(); //we have to use renderAll instead of requestRenderAll here, or the coords aren't upated when moving the tools.
    let textbox = helper.getExistingCanvasObject(pageItem);
    invariant(
      textbox,
      `textbox not found for pageItemId: ${pageItem.get('id')}`,
    );
    textbox.setCoords();
    this._setTextSelection(textbox);
    if (this.pageItemUpdated !== undefined && style) {
      this.pageItemUpdated(textbox, true);
    }
  }

  replaceText(pageItem, newText) {
    let isDefaultCaption = false;
    let helper = new PageRendererRichTextHelper(
      this.currentPageDimensions,
      this._canvas,
      this._ratioWidth,
      this._interactive,
      nodeRender,
      this.isMobile,
    );
    if (newText.replace(' ', '') == '') {
      isDefaultCaption = true;
      newText = PicabooConstants.DEFAULT_RT_CAPTION;
    }
    let textbox = helper.replaceText(pageItem, newText, isDefaultCaption);
    if (this.pageItemUpdated !== undefined) {
      this.pageItemUpdated(textbox, true);
    }
    return textbox;
  }

  redrawPageItem(pageItem) {
    let canvas = this._canvas;
    let objects = canvas.getObjects();
    for (let obj of objects) {
      if (obj.pageItemVO.get('id') === pageItem.get('id')) {
        if (
          obj.pageItemVO.get('itemType') !=
          PicabooConstants.ITEM_TYPES.RICH_TEXT
        ) {
          obj.set({ scaleX: 1, scaleY: 1 });
        }

        this._drawPageItem(pageItem, () => {
          this.pageItemUpdated(obj, false);
          canvas.requestRenderAll();
        });
      }
    }
  }

  updateImages(images) {
    this._images = images;
  }

  updateWrap(wrap) {
    this._wrap = wrap;
  }

  drawPage(
    page,
    pageItems,
    images,
    stateSelection,
    renderCompleteCallback,
    register = true,
    forceRender = false,
  ) {
    this.loadDefaultFonts(page, pageItems).then(() => {
      this._page = page;
      this._images = images;
      this._selection = stateSelection;
      this._register = register;
      this.renderCompleteCallback = renderCompleteCallback;

      if (!page) {
        return;
      }

      let dim = this.currentPageDimensions;
      this._ratioBase = page.get('ratioBase') || 1;
      this._ratioWidth = dim.width / this._ratioBase;

      if (register) {
        PageRendererRegistry.instance.registerPageRenderer(
          page.get('id'),
          this,
        );
      }

      let canvas = this._canvas;
      if (canvas.pageID && canvas.pageID !== page.get('id')) {
        canvas.clear();
      }
      canvas.pageID = page.get('id');

      //Let's add a 'pageItemVO' property to all fabricjs objects
      fabric.Object.prototype.toObject = (function (toObject) {
        return function () {
          return fabric.util.object.extend(toObject.call(this), {
            pageItemVO: this.pageItemVO,
          });
        };
      })(fabric.Object.prototype.toObject);

      canvas.setWidth(dim.width);
      canvas.setHeight(dim.height);

      let graphicItem =
        page.getIn(['background', 'graphicItem']) ||
        page.get('background')?.graphicItem;

      const solidBackgroundColor = function (defaultColor = undefined) {
        let color = page.getIn(['background', 'color']);
        if (color === undefined) {
          color = page.get('background')?.color;
        }

        if (color === undefined && defaultColor !== undefined) {
          color = defaultColor;
        }

        if (color || color === 0) {
          canvas.backgroundImage = 0;
          let c = GraphicItemUtils.toColor(color);
          if (!global.isRenderer && this._interactive) {
            canvas.setBackgroundColor(c, canvas.requestRenderAll.bind(canvas));
          } else {
            canvas.setBackgroundColor(c);
          }
        }
        this._sortPageItemRefs(page, pageItems);
      }.bind(this);

      if (graphicItem) {
        let giID = graphicItem;
        let gi = pageItems.get(giID);

        //Sometimes themes have background graphic items with no imageID
        if (gi && gi.get('imageID')) {
          let helper = new PageRendererGraphicItemHelper(
            this.currentPageDimensions,
            this._canvas,
            this._images,
            this._imagePath,
            this._ratioWidth,
            this._productInfo && this._productInfo.get('resolutionFactor'),
            this._interactive,
            nodeRender,
            this.isMobile,
          );

          helper.drawBackgroundImage(gi).then(
            function (ret) {
              if (ret.fail) {
                console.error('Missing Image', gi ? gi.toJS() : null);
                solidBackgroundColor(0xffffff);
                return;
              }
              const image = ret.image;

              if (image !== null) {
                if (
                  !global.isRenderer &&
                  image.pageItemVO.get('pageID') !== this._canvas.pageID
                ) {
                  //we changed pages during the load, so just drop this
                  return;
                }
                canvas.setBackgroundImage(image);

                if (forceRender || (!global.isRenderer && this._interactive)) {
                  canvas.requestRenderAll();
                }
              }

              this._sortPageItemRefs(page, pageItems);
            }.bind(this),
          );
        } else {
          this._sortPageItemRefs(page, pageItems);
        }
      } else {
        solidBackgroundColor(0xffffff);
      }

      let pattern =
        page.getIn(['background', 'pattern']) ||
        page.get('background')?.pattern;

      pattern = pattern ?? '';
      switch (pattern) {
        case PicabooConstants.PAGE_BACKGROUND_PATTERNS.COLLEGE_RULE:
          this.drawPatternCollegeRule();
          break;
        case PicabooConstants.PAGE_BACKGROUND_PATTERNS.NARROW_RULE:
          this.drawPatternNarrowRule();
          break;
        case PicabooConstants.PAGE_BACKGROUND_PATTERNS.WIDE_RULE:
          this.drawPatternWideRule();
          break;
        case PicabooConstants.PAGE_BACKGROUND_PATTERNS.GRID_LINES:
          this.drawPatternGridLines();
          break;
        case PicabooConstants.PAGE_BACKGROUND_PATTERNS.GRID_DOTS:
          this.drawPatternGridDots();
          break;
        default:
          canvas.set({ backgroundImage: null });
          break;
      }
    });

    //Turn off auto-rendering for performance while we build our page
    // canvas.renderOnAddRemove = false;
  }

  drawPatternCollegeRule() {
    this.drawBackgroundLines(
      PicabooConstants.PAGE_BACKGROUND_PATTERNS.STROKE_COLOR,
      true,
      false,
      9 / 32,
    );
  }

  drawPatternNarrowRule() {
    this.drawBackgroundLines(
      PicabooConstants.PAGE_BACKGROUND_PATTERNS.STROKE_COLOR,
      true,
      false,
      1 / 4,
    );
  }

  drawPatternWideRule() {
    this.drawBackgroundLines(
      PicabooConstants.PAGE_BACKGROUND_PATTERNS.STROKE_COLOR,
      true,
      false,
      11 / 32,
    );
  }

  drawPatternGridLines() {
    this.drawBackgroundLines(
      PicabooConstants.PAGE_BACKGROUND_PATTERNS.STROKE_COLOR,
      true,
      true,
      1 / 4,
      -1 / 8,
      -1 / 8,
      0,
      0,
    );
  }

  drawPatternGridDots() {
    this.drawBackgroundDots(
      PicabooConstants.PAGE_BACKGROUND_PATTERNS.STROKE_COLOR,
      1 / 4,
      -1 / 8,
      -1 / 8,
      0,
      0,
    );
  }

  drawBackgroundLines(
    strokeColor = '#C2C2C2',
    showHorizontalLines = true,
    showVerticalLines = false,
    spacingInches = 9 / 32,
    marginLeftInches = 0,
    marginTopInches = 0,
    marginRightInches = 0,
    marginBottomInches = 0,
  ) {
    const parsed = z
      .object({
        strokeColor: z.string().min(3),
        showHorizontalLines: z.boolean(),
        showVerticalLines: z.boolean(),
        spacingInches: z.number().positive(),
        marginLeftInches: z.number(),
        marginTopInches: z.number(),
        marginRightInches: z.number(),
        marginBottomInches: z.number(),
      })
      .safeParse({
        strokeColor,
        showHorizontalLines,
        showVerticalLines,
        spacingInches,
        marginLeftInches,
        marginTopInches,
        marginRightInches,
        marginBottomInches,
      });
    if (!parsed.success) {
      console.error('Invalid parameters for drawBackgroundLines', parsed.error);
      return;
    }

    const canvas = this._canvas;

    const spacing = this.inchesToPixels(spacingInches);
    const marginTop = this.inchesToPixels(marginTopInches);
    const marginBottom = this.inchesToPixels(marginBottomInches);
    const marginRight = this.inchesToPixels(marginRightInches);
    const marginLeft = this.inchesToPixels(marginLeftInches);

    const patternSourceCanvas = new fabric.StaticCanvas();
    patternSourceCanvas.width = this._canvas.width - marginLeft - marginRight;
    patternSourceCanvas.height = this._canvas.height - marginTop - marginBottom;

    patternSourceCanvas.on('after:render', function () {
      canvas.requestRenderAll();
    });

    const strokeWidth = Math.max(this.inchesToPixels(2 / 72), 1);

    if (showHorizontalLines) {
      for (let i = spacing; i < patternSourceCanvas.height; i += spacing) {
        const coords = [
          0,
          i - strokeWidth,
          patternSourceCanvas.width,
          i - strokeWidth,
        ];
        patternSourceCanvas.add(
          new fabric.Line(coords, {
            strokeWidth: strokeWidth,
            stroke: strokeColor,
          }),
        );
      }
    }

    if (showVerticalLines) {
      for (let i = spacing; i < patternSourceCanvas.width; i += spacing) {
        const coords = [
          i - strokeWidth,
          0,
          i - strokeWidth,
          patternSourceCanvas.height,
        ];
        patternSourceCanvas.add(
          new fabric.Line(coords, {
            strokeWidth: strokeWidth,
            stroke: strokeColor,
          }),
        );
      }
    }

    const element = patternSourceCanvas.getElement();
    element.width = patternSourceCanvas.width;
    element.height = patternSourceCanvas.height;

    const pattern = new fabric.Pattern({
      source: element,
      repeat: 'no-repeat',
      offsetX: marginLeft,
      offsetY: marginTop,
    });

    const shape = new fabric.Rect({
      fill: pattern,
      width: canvas.width,
      height: canvas.height,
    });

    canvas.set({ backgroundImage: shape });
  }

  drawBackgroundDots(
    strokeColor = '#C2C2C2',
    spacingInches = 1 / 4,
    marginLeftInches = 0,
    marginTopInches = 0,
    marginRightInches = 0,
    marginBottomInches = 0,
  ) {
    const parsed = z
      .object({
        strokeColor: z.string().min(3),
        spacingInches: z.number().positive(),
        marginLeftInches: z.number(),
        marginTopInches: z.number(),
        marginRightInches: z.number(),
        marginBottomInches: z.number(),
      })
      .safeParse({
        strokeColor,
        spacingInches,
        marginLeftInches,
        marginTopInches,
        marginRightInches,
        marginBottomInches,
      });
    if (!parsed.success) {
      console.error('Invalid parameters for drawBackgroundDots', parsed.error);
      return;
    }

    const canvas = this._canvas;

    const spacing = this.inchesToPixels(spacingInches);
    const marginTop = this.inchesToPixels(marginTopInches);
    const marginBottom = this.inchesToPixels(marginBottomInches);
    const marginRight = this.inchesToPixels(marginRightInches);
    const marginLeft = this.inchesToPixels(marginLeftInches);

    const patternSourceCanvas = new fabric.StaticCanvas();
    patternSourceCanvas.width = this._canvas.width - marginLeft - marginRight;
    patternSourceCanvas.height = this._canvas.height - marginTop - marginBottom;

    patternSourceCanvas.on('after:render', function () {
      canvas.requestRenderAll();
    });

    const strokeWidth = Math.max(this.inchesToPixels(2 / 72), 1);

    for (let i = spacing; i < patternSourceCanvas.height; i += spacing) {
      for (let j = spacing; j < patternSourceCanvas.width; j += spacing) {
        patternSourceCanvas.add(
          new fabric.Circle({
            left: j - strokeWidth,
            top: i - strokeWidth,
            radius: strokeWidth,
            fill: strokeColor,
          }),
        );
      }
    }

    const element = patternSourceCanvas.getElement();
    element.width = patternSourceCanvas.width;
    element.height = patternSourceCanvas.height;

    const pattern = new fabric.Pattern({
      source: element,
      repeat: 'no-repeat',
      offsetX: marginLeft,
      offsetY: marginTop,
    });

    const shape = new fabric.Rect({
      fill: pattern,
      width: canvas.width,
      height: canvas.height,
    });

    canvas.set({ backgroundImage: shape });
  }

  /**
   * Converts inches to pixels for the current product and canvas height.
   * @param inches
   * @returns {number}
   */
  inchesToPixels(inches) {
    const heightInches = this._productInfo.get('printedHeight');
    const pixelsPerInch = this._canvas.height / heightInches;

    return inches * pixelsPerInch;
  }

  loadDefaultFonts(page, pageItems) {
    return new Promise((resolve) => {
      let fontNames = [];
      let fonts = [
        'Archivo:n4,i4,n7,i7',
        'Arvo:n4,i4,n7,i7',
        'Barlow Semi Condensed:n4,i4,n7,i7',
        'Caveat:n4,i4,n7,i7',
        'Courier Prime:n4,i4,n7,i7',
        'EB Garamond:n4,i4,n7,i7',
        'Exo 2:n4,i4,n7,i7',
        'Istok Web:n4,i4,n7,i7',
        'Lexend:n4,i4,n7,i7',
        'Lexend Deca:n4,i4,n7,i7',
        'Libre Baskerville:n4,i4,n7,i7',
        'Montserrat:n4,i4,n7,i7',
        'Nanum Myeongjo:n4,i4,n7,i7',
        'NotDef:n4,i4,n7,i7',
        'Parisienne:n4,i4,n7,i7',
        'Playfair Display:n4,i4,n7,i7',
        'Plus Jakarta Sans:n4,i4,n7,i7',
        'PlusJakartaSansLight:n4,i4,n7,i7',
        'Prata:n4,i4,n7,i7',
        'Quattrocento:n4,i4,n7,i7',
        'Quicksand:n4,i4,n7,i7',
        'Spectral:n4,i4,n7,i7',
        'Tangerine:n4,i4,n7,i7',
      ]; // Preload all fonts to prevent text overflow beyond bounding box on changing font family and any other text styling issues or FOUT (Flash of Unstyled Text).
      // Font names need to match the font-config in index.html

      if (nodeRender) {
        resolve();
      } else {
        let WebFont = require('webfontloader');
        WebFont.load({
          custom: {
            families: fonts,
          },

          active: function () {
            resolve();
          },
          inactive: function () {
            resolve();
          },
        });
      }
    });
  }

  _sortPageItemRefs(page, pageItems) {
    const pageItemRefs = page.get('pageItems');
    this._pageItems = [];

    let zindex = 0;
    let onTop = [];
    pageItemRefs.forEach((id) => {
      const pageItem = pageItems.get(id);
      if (pageItem) {
        if (
          pageItem.get('type') === PicabooConstants.WELL_TYPES.ALWAYS_ON_TOP
        ) {
          onTop.push(id);
        } else {
          this._pageItems.push(pageItem);
          this._setZIndex(pageItem, zindex);
          ++zindex;
        }
      }
    });

    onTop.forEach((id) => {
      const pageItem = pageItems.get(id);
      if (pageItem) {
        this._pageItems.push(pageItem);
        this._setZIndex(pageItem, zindex);
        ++zindex;
      }
    });

    if (
      (global.isRenderer || !this._interactive) &&
      !this._canvas.renderOnAddRemove
    ) {
      async.eachSeries(
        this._pageItems,
        this._drawPageItem.bind(this),
        (err) => {
          if (!global.isRenderer) {
            this._canvas.renderOnAddRemove = true;
            this._canvas.requestRenderAll();
          }
          this._postProcess();
        },
      );
    } else {
      async.each(this._pageItems, this._drawPageItem.bind(this), (err) => {
        this._cleanupRemovedItems();
        this._postProcess();
      });
    }
  }

  _postProcess() {
    if (!global.isRenderer) {
      this._drawOverlay().then(() => {
        this.renderCompleteCallback();
      });
    } else {
      this.renderCompleteCallback();
    }
  }

  /**
   * Applies the foilColor to the page item if appropriate.
   * @param pi
   * @returns {*}
   * @private
   */
  _applyFoilColor(pi) {
    if (!global.isRenderer && false) {
      let foilColor = this._foilColor;
      let coverColors = this._productInfo.get('coverColors');
      let color = coverColors
        .filter((colorMap) => colorMap.get('id') === foilColor)
        .map((color) => decimalToRgba(hexToDecimal(color.get('color'))));
      pi = pi.set('foilColor', color.get(0));
    }

    return pi;
  }

  _drawPageItem(pi, complete) {
    if (!pi.has('rotation') || pi.get('rotation') === null) {
      pi = pi.set('rotation', 0);
    }

    let x = pi.get('positionX') * this._ratioWidth;
    let y = pi.get('positionY') * this._ratioWidth;
    let objDim = this._getObjectDimensions(pi);

    let itemWidth = objDim.width;
    let itemHeight = objDim.height;
    let helper = undefined;

    // Do a sanity check. These will hose anything after it. And they won't actually print anyway
    if (pi.get('width') === null || pi.get('height') === null) {
      complete && complete();
      return;
    }

    switch (pi.get('itemType')) {
      case PicabooConstants.ITEM_TYPES.SHAPE:
      case PicabooConstants.ITEM_TYPES.LINE:
        helper = new PageRendererShapeHelper(
          this.currentPageDimensions,
          this._canvas,
          this._ratioWidth,
          this._interactive,
          nodeRender,
          this.isMobile,
        );
        helper.drawShape(pi, itemWidth, itemHeight, x, y).then(
          function (group) {
            if (
              !nodeRender &&
              !global.isRenderer &&
              group.pageItemVO.get('pageID') !== this._canvas.pageID
            ) {
              //we changed pages during the load, so just drop this
              return;
            }
            if (!this._page.get('canAddImage')) {
              group.selectable = false;
              group.hoverCursor = 'default';
            }
            this._addToCanvas(group);
            complete && complete();
          }.bind(this),
        );
        break;
      case PicabooConstants.ITEM_TYPES.GRAPHICITEM:
        if (pi.get('imageID')) {
          let imageID = pi.get('imageID');
          let rotated =
            pi.get('image_rotation') == 90 || pi.get('image_rotation') == 270;
          let piDim = {
            x: pi.get('image_positionX'),
            y: pi.get('image_positionY'),
            width: rotated ? pi.get('image_height') : pi.get('image_width'),
            height: rotated ? pi.get('image_width') : pi.get('image_height'),
          };
          let img = this._images.get(imageID);

          if (this._withWarnings) {
            let isLowRes = GraphicItemUtils.isLowRes(
              piDim,
              img,
              this._productInfo && this._productInfo.get('resolutionFactor'),
            );

            pi = pi.set('isLowRes', isLowRes);
            this._checkLowRes(pi, isLowRes);
          }

          helper = new PageRendererGraphicItemHelper(
            this.currentPageDimensions,
            this._canvas,
            this._images,
            this._imagePath,
            this._ratioWidth,
            this._productInfo && this._productInfo.get('resolutionFactor'),
            this._interactive,
            nodeRender,
            this.isMobile,
          );
          helper
            .drawGraphicItem(pi, itemWidth, itemHeight, x, y)
            .then(
              function (ret) {
                let group = ret.group;
                if (
                  !global.isRenderer &&
                  !nodeRender &&
                  group.pageItemVO.get('pageID') !== this._canvas.pageID
                ) {
                  //we changed pages during the load, so just drop this
                  return;
                }
                this._addToCanvas(group);
                complete && complete();
              }.bind(this),
            )
            .catch((e) => {
              console.error(
                'drawGraphicItem error for image id ' +
                  pi.get('imageID') +
                  '. Error: ' +
                  e,
              );
            });
        } else {
          if (!nodeRender && !global.isRenderer && this._interactive) {
            helper = new PageRendererEmptyWellHelper(
              this.currentPageDimensions,
              this._canvas,
              this._ratioWidth,
              this._interactive,
              nodeRender,
              this.isMobile,
            );
            helper.drawEmptyWell(pi, itemWidth, itemHeight, x, y).then(
              function (group) {
                if (
                  !nodeRender &&
                  group.group.pageItemVO.get('pageID') !== this._canvas.pageID
                ) {
                  //we changed pages during the load, so just drop this
                  return;
                }
                this._addToCanvas(group.group);
                complete && complete();
              }.bind(this),
            );
          } else {
            complete && complete();
          }
        }
        break;
      case PicabooConstants.ITEM_TYPES.RICH_TEXT:
        helper = new PageRendererRichTextHelper(
          this.currentPageDimensions,
          this._canvas,
          this._ratioWidth,
          this._interactive,
          nodeRender,
          this.isMobile,
        );

        helper
          .drawCaption(
            pi,
            itemWidth,
            itemHeight,
            x,
            y,
            null,
            null,
            this._productInfo,
          )
          .then(
            function (ret) {
              if (!ret) {
                console.warn('drawRichText returned null value');
              }

              let keyUp = function (e) {
                if (!this.keypresses) {
                  this.keypresses = 1;
                } else {
                  this.keypresses++;
                }

                if (this.keypresses % 5 === 0) {
                  //force update to set position of editor controls
                  this._setTextSelection(ret.textbox);
                }
                if (this.keypresses % 25 !== 0) {
                  clearTimeout(this.richTextTimeout);
                }
                this.richTextTimeout = setTimeout(() => {
                  this._setTextSelection(ret.textbox);
                  if (this._interactive === true && this._withWarnings) {
                    this._spellCheckRichText(
                      ret.textbox,
                      e.target.value,
                      pi.get('ignoredWords')?.toJS() || [],
                    );
                  }
                }, 2000);
              }.bind(this);

              if (ret && ret.textbox && ret.addEvents) {
                if (
                  !nodeRender &&
                  !global.isRenderer &&
                  ret.textbox.pageItemVO.has('pageID') &&
                  ret.textbox.pageItemVO.get('pageID') !== this._canvas.pageID
                ) {
                  //we changed pages during the load, so just drop this
                  return;
                }
                this._addToCanvas(ret.textbox);
                ret.textbox.setCoords(); //since size of caption box probably changed

                ret.textbox.on(
                  'selection:changed',
                  function (e) {
                    this._setTextSelection(ret.textbox);
                  }.bind(this),
                );

                ret.textbox.on(
                  'editing:entered',
                  function (e) {
                    window.addEventListener('keyup', keyUp);
                    if (
                      ret.textbox.text == PicabooConstants.DEFAULT_RT_CAPTION
                    ) {
                      ret.textbox.removeChars(0, 14);
                      ret.textbox.hiddenTextarea.value = '';
                      ret.textbox.set('text', '');
                      ret.textbox.setSelectionStart(0);
                      ret.textbox.setSelectionEnd(0);
                    }

                    this._setTextSelection(ret.textbox);
                  }.bind(this),
                );

                ret.textbox.on('editing:exited', (e) => {
                  window.removeEventListener('keyup', keyUp);
                  if (this.pageItemUpdated !== undefined) {
                    if (
                      ret.textbox.text == undefined ||
                      ret.textbox.text.length == 0
                    ) {
                      ret.textbox.set(
                        'text',
                        PicabooConstants.DEFAULT_RT_CAPTION,
                      );
                      ret.textbox.styles[0] = undefined; //{0: ret.textbox.styles[0][0]};
                    }
                  }
                  if (this.pageItemUpdated !== undefined) {
                    this.pageItemUpdated(ret.textbox, true);
                  }
                  this._textSelectionChanged && this._textSelectionChanged([]);
                });
              }

              complete && complete();
            }.bind(this),
          );
        break;
      default:
        complete && complete();
    }
  }

  _setTextSelection(textbox) {
    if (this._textSelectionChanged) {
      let selectedStyles = textbox.getSelectionStyles();

      if (
        selectedStyles.length === 0 &&
        textbox.selectionStart === textbox.selectionEnd
      ) {
        //no selection, only the cursor

        let start = textbox.selectionStart;
        if (textbox.selectionStart > 1) {
          start--;
        }

        selectedStyles = textbox.getSelectionStyles(start, start + 1);
      }

      this._textSelectionChanged(
        selectedStyles,
        textbox.selectionStart,
        textbox.selectionEnd,
      );
    }
  }

  _spellCheckRichText(textbox, stringToCheck, ignoredWords) {
    if (!textbox || !stringToCheck || !this._withWarnings) {
      return;
    }
    const spellingWarnings = this._projectWarnings.get('spelling');
    let newWarnings = this._projectWarnings;
    newWarnings = newWarnings.delete('spelling');

    let newSpellWarnings = [];
    const textboxId = textbox.pageItemVO.get('id');
    const textboxPageId = textbox.pageItemVO.get('pageID');
    spellingWarnings.map((spell) => {
      if (spell.get('pageItemID') !== textboxId) {
        newSpellWarnings.push(spell);
      }
    });

    // remove any words from ignoredWords that are no longer in the string
    ignoredWords.filter((wordToCheck) => {
      return stringToCheck
        .toLowerCase()
        .split(' ')
        .includes(wordToCheck.toLowerCase());
    });

    zoomServices
      .spellCheckStringService(stringToCheck, ignoredWords)
      .then((results) => {
        let spellcheck = fromJS(results);
        if (spellcheck?.size > 0) {
          for (const result of results) {
            if (!ignoredWords.includes(result.word.toLowerCase())) {
              const warning = {
                word: result.word,
                pageID: textboxPageId,
                pageItemID: textboxId,
                spreadID: this.pageSpreadID,
                suggestions: List(result.suggestions),
              };
              newSpellWarnings.push(Map(warning));
            }
          }
        }
        newWarnings = newWarnings.set('spelling', List(newSpellWarnings));
        this._handleUpdateWarnings(newWarnings);

        textbox.pageItemVO = textbox.pageItemVO.set('spellCheck', spellcheck);
        textbox.pageItemVO = textbox.pageItemVO.set(
          'ignoredWords',
          ignoredWords,
        );
        this.pageItemUpdated(textbox, false);
      });
  }

  _checkLowRes(pi, isLowRes, projectWarnings = this._projectWarnings) {
    if (!pi || projectWarnings === undefined || !this._withWarnings) {
      return;
    }

    let resolutionWarnings = projectWarnings.get('lowResolution');
    let updatedWarnings = projectWarnings;
    let newResolutionWarnings = [];
    resolutionWarnings?.forEach((resolutionWarning) => {
      if (resolutionWarning.get('pageItemID') !== pi.get('id')) {
        newResolutionWarnings.push(Map(resolutionWarning));
      }
    });
    if (isLowRes) {
      let newResolutionWarning = {
        pageID: pi.get('pageID'),
        pageItemID: pi.get('id'),
        spreadID: this.pageSpreadID,
      };
      newResolutionWarnings.push(fromJS(newResolutionWarning));
    }
    updatedWarnings = updatedWarnings.set(
      'lowResolution',
      List(newResolutionWarnings),
    );
    this._handleUpdateWarnings(updatedWarnings);
    this._canvas.requestRenderAll();
    return isLowRes;
  }

  _renderRequestStillValid() {
    if (
      this._pageRenderID === undefined ||
      this._page === undefined ||
      !this._register
    ) {
      return true;
    }
    let currentPageRenderID = PageRenderer.getCurrentPageRenderID(
      this._page.get('id'),
    );
    let valid = this._pageRenderID === currentPageRenderID;
    if (!valid) {
      console.warn(
        'INVALID REQUEST instance id=' +
          this._pageRenderID +
          ' static id=' +
          currentPageRenderID,
      );
    }
    return valid;
  }

  _cleanupRemovedItems() {
    let itemsToRemove = [];

    this._canvas.getObjects().forEach(
      function (canvasItem) {
        const itemOnPage = this._pageItems.some(function (pageItem) {
          return canvasItem.pageItemVO.get('id') === pageItem.get('id');
        });

        if (!itemOnPage) {
          itemsToRemove.push(canvasItem);
        }
      }.bind(this),
    );

    itemsToRemove.forEach(
      function (item) {
        this._canvas.remove(item);
      }.bind(this),
    );
  }

  _addToCanvas(obj) {
    if (!this._renderRequestStillValid()) {
      return;
    }

    let canvas = this._canvas;
    let pi = obj.pageItemVO;
    let zindex = this._getZIndex(pi.get('id'));
    // if(zindex === 0) {

    let objects = this._canvas.getObjects();
    let existingItem = objects.find(function (canvasItem, index, array) {
      if (canvasItem.pageItemVO) {
        return canvasItem.pageItemVO.get('id') === pi.get('id');
      } else {
        console.warn('canvasItem.pageItemVO is undefined', canvasItem);
      }
    });

    if (!existingItem) {
      // if (this.isMobile) {
      //     obj.hasControls = false;
      //     obj.lockMovementX = true;
      //     obj.lockMovementY = true;
      // }
      canvas.add(obj);
    } else {
      if (!global.isRenderer && this._interactive) {
        canvas.requestRenderAll();
      }
    }

    if (!global.isRenderer) {
      // Only called from the editor. The renderer calls adjustZIndexes() once everything is done
      this._adjustZIndexes();
      this._setSelection();
    }
  }

  //post processing

  _drawOverlay() {
    return new Promise((resolve, reject) => {
      if (!this._overlay) {
        resolve();
        return;
      }

      let url = this._overlay;
      let backgroundColor = false;
      if (isFabric(this._style)) {
        if (global.isRenderer) {
          resolve(false);
          return;
        }

        url = getFabricImage(
          this._foilColor ? this._foilColor : this._coverColor,
        );
        backgroundColor = this._overlay;
      }

      FabricUtils.loadImage(
        url,
        function (image) {
          if (!this._renderRequestStillValid()) {
            return;
          }

          if (!image) {
            console.error('Could not load overlay', url);
            reject();
            return;
          }

          //let fabricRect = new fabric.Rect();

          //let scaleX = this.currentPageDimensions.width / image.naturalWidth;
          //let scaleY = this.currentPageDimensions.height / image.naturalHeight;

          // let fabricImage = new fabric.Image(image);
          // fabricImage.width = this.currentPageDimensions.width; // scaleX;
          // fabricImage.height = this.currentPageDimensions.height;// scaleY;
          // //fabricImage.scaleX = scaleX;
          // //fabricImage.scaleY = scaleY;
          //
          // fabricImage.pageItemVO = fromJS({id: "overlayID"});
          // fabricImage.selectable = false;
          // fabricImage.hasControls = true;
          // fabricImage.hasBorders = true;
          //
          // if ( !(isFabric(this._style) && global.isRenderer)) {
          //    this._canvas.setBackgroundImage(fabricImage);
          // }
          //
          // if ( backgroundColor ) {
          //    this._canvas.setBackgroundColor(backgroundColor);
          // }
          //
          this._canvas.setBackgroundColor({ source: image, repeat: 'repeat' });
          this._canvas.requestRenderAll();
          resolve();
        }.bind(this),
        this,
        'Anonymous',
      );
    });
  }

  _adjustZIndexes() {
    let onTop = [];
    const canvasObjects = this._canvas.getObjects();
    this._pageItems.forEach(
      function (pi) {
        const index = this._getZIndex(pi.get('id'));
        for (let i = 0, count = canvasObjects.length; i < count; i++) {
          let canvasItem = canvasObjects[i];
          if (canvasItem.pageItemVO) {
            if (canvasItem.pageItemVO.get('id') === pi.get('id')) {
              if (
                pi.get('type') === PicabooConstants.WELL_TYPES.ALWAYS_ON_TOP
              ) {
                canvasItem.moveTo(canvasObjects.length);
              } else {
                if (index !== i) {
                  canvasItem.moveTo(index);
                }
              }
            }
          } else {
            console.warn('missing canvasItem.pageItemVO', canvasItem);
          }
        }
      }.bind(this),
    );
  }

  _setSelection() {
    // if (this._selection !== null && this._selection.pageItems !== null && this._selection.pageItems.length > 0) {
    //     let pageItemID = this._selection.pageItems[0];
    //     let selectedObject = this._canvas.getObjects().find(function(canvasItem, index, array) {
    //         return canvasItem.pageItemVO.get('id') === pageItemID;
    //     });
    //
    //     if (selectedObject) {
    //         this._canvas.setActiveObject(selectedObject);
    //     }
    // }
  }

  _getZIndex(piID) {
    if (!piID) return 0;
    if (!this._zindexes) return 0;

    return this._zindexes[piID] || 0;
  }

  _setZIndex(pi, index) {
    if (!this._zindexes) {
      this._zindexes = {};
    }
    this._zindexes[pi.get('id')] = index;
  }

  _getObjectDimensions(pi, scaleX = 1, scaleY = 1) {
    let dim = this.currentPageDimensions;
    return {
      width: pi.get('width') * this._ratioWidth * scaleX,
      height: pi.get('height') * this._ratioWidth * scaleY,
    };
  }
}
